import { Field } from "@fluentui/react-components"
import type { ReactElement } from "react"
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form"

// Campo de formulario estándar de la consola — docs/05 §3: validación individual
// por campo, siempre inline. El control se pinta en rojo (validationState="error")
// y el motivo va en el helper text (validationMessage). El timing blur → onChange
// lo fija el useForm del formulario (mode: "onBlur", reValidateMode: "onChange").

export interface FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >
  required?: boolean
  // Texto de ayuda neutro; se oculta mientras el campo tenga error
  hint?: string
  children: (field: ControllerRenderProps<TFieldValues, TName>, invalid: boolean) => ReactElement
}

export const FormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  rules,
  required,
  hint,
  children,
}: FormFieldProps<TFieldValues, TName>): ReactElement => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    render={({ field, fieldState }) => (
      <Field
        label={label}
        required={required}
        hint={fieldState.invalid ? undefined : hint}
        validationState={fieldState.invalid ? "error" : "none"}
        validationMessage={fieldState.error?.message}
      >
        {children(field, fieldState.invalid)}
      </Field>
    )}
  />
)
